import { useTranslation } from 'next-i18next';

import { titleCase } from '@utils/plp/utils/transforms';

import type { ColourPickerColour } from '../ColourPickerContext';
import { useColourPickerContext } from '../ColourPickerContext';
import { preloadResponsiveImage } from './preloadResponsiveImage';

const GREIGE_HEX = '#B0A999';

export interface ColourSwatchModel {
  colour: ColourPickerColour;
  displayColourName: string;
  hasImage: boolean;
  hasHex: boolean;
  isNoData: boolean;
  disabledLabel: string;
  onSelect: () => void;
  onPreload: () => void;
}

/**
 * Shared colour resolution + actions for both PDP and card swatch variants.
 */
export function useColourSwatchModel(
  colourId: number
): ColourSwatchModel | null {
  const { t } = useTranslation('pdp');
  const { state, actions, preloadImageSizes } = useColourPickerContext();
  const colour = state.colours.find((c) => c.id === colourId);

  if (!colour) return null;

  const hasImage = Boolean(colour.swatchImage);
  const firstHex = colour.hexColours?.[0];
  const hasHex = Boolean(firstHex);
  const isGreigeHex =
    firstHex?.toUpperCase().trim() === GREIGE_HEX.toUpperCase();
  const isNoData = !hasImage && (!hasHex || isGreigeHex);
  const displayColourName = titleCase(colour.name);

  const handlePreload = () => {
    const url = colour.productImage;
    if (typeof url === 'string' && url) {
      preloadResponsiveImage(url, preloadImageSizes);
    }
  };

  return {
    colour,
    displayColourName,
    hasImage,
    hasHex,
    isNoData,
    disabledLabel: colour.isDisabled
      ? ` (${t('product.colour.notAvailableForSelectedSize')})`
      : '',
    onSelect: () => actions.onSelectColour(colour.id),
    onPreload: handlePreload,
  };
}
